import { inject, Injectable } from "@angular/core";
import { ClToken2Service } from "@carnegie-learning/data-access-angular-fido";

/**
 * @class CookieService
 * @description service class containing cookie methods that have been abstracted
 */
@Injectable({ providedIn: "root" })
export class CookieService {
    private clToken2Service = inject(ClToken2Service);

    constructor() {}

    /**
     * @method set
     * @description stores the specified cookie data
     * @param {string} cname - cookie key
     * @param {string} cvalue - cookie value
     * @param {int} exdays - # of days cookie is valid
     */
    set(cname: string, cvalue: string, exdays: number): void {
        const d = new Date();
        d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
        const expires = "expires=" + d.toUTCString();
        document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    }

    /**
     * @method get
     * @description retrieves cookie value specified by the cookie name/key
     * @param {string} cname - cookie key
     * @returns {any}
     */
    get(cname: string): any {
        if (cname === "CL_TOKEN2") {
            try {
                return this.clToken2Service.getClToken2();
            } catch (e) {
                console.error(e);
                return "";
            }
        }
        const name = cname + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                // continue searching throughout the cookie string if value is empty
                const value = c.substring(name.length, c.length);
                if (value) {
                    // only return a cookie with a value else keep iterating
                    return value;
                }
            }
        }
        return "";
    }

    /**
     * @method getAll
     * @description retrieves an array of cookies
     * @returns {string[]}
     */
    getAll(): string[] {
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(";");
        return ca;
    }

    /**
     * @method delete
     * @description deletes cookie specified by the cookie name/key by expiring the cookie
     * @param {string} cname
     */
    delete(cname: string): void {
        if (cname === "CL_TOKEN2" || cname === "CL_TOKEN") {
            // do nothing since we don't want to mess with authentication
        } else {
            const d = new Date();
            const cvalue = "";
            d.setTime(d.getTime() + -30 * 24 * 60 * 60 * 1000); // 30 days old
            const expires = "expires=" + d.toUTCString();
            document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
        }
    }

    /**
     * @method deleteAll
     * @description deletes all cookies by expiring the cookie
     */
    deleteAll(): void {
        const cookies = document.cookie.split(";");

        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            let domain = (<any>window).location.hostname;
            if (name.indexOf("CL_") > -1) {
                // parent domain cookie, need to change
                domain =
                    "." +
                    (<any>window).location.hostname.substring(
                        (<any>window).location.hostname.indexOf(".") + 1
                    );
            }
            const d = new Date();
            d.setTime(d.getTime() + -30 * 24 * 60 * 60 * 1000); // 30 days old
            document.cookie =
                name + "=" + "" + ";" + d + ";path=/;domain=" + domain + ";";
        }
    }
}
